import { createSlice } from '@reduxjs/toolkit';
import { ADDITIONAL_VEHICLE_STEPS } from '../pages/additionals/vehicle-request/create-vehicle-request-steps';
import { getCurrentFiscalYear } from '../pages/VehicleRequisition/utils/VehicleRequisitionUtils';

const additionalsInitialState = {
  currentAdditionalAction: '',
  additionalFilters: [],
  paginationState: {
    limit: 25000,
    offset: 0,
    currentPage: 1,
    itemsPerPage: 25,
    isReset: false,
    order: null,
  },
  customerAccountList: [],
  selectedCustomerAccount: {},
  previousStandardItem: {
    id: null,
    standardItemNumber: null,
    title: null,
  },
  selectedOptions: [],
  currentStep: ADDITIONAL_VEHICLE_STEPS.GARAGE,
  selectedStandardItem: null,
  useSameSin: true,
  lastSin: null,
  status: '',
  agencyGuidanceDocument: {},
  additionalFiscalYear: getCurrentFiscalYear(),
  garageAddress: null,
  usedForTowing: false,
  projectedTowingWeight: {
    value: null,
    unit: 'lbs',
  },
  additionalRequestList: { rows: [], count: 0, hasMore: false },
  additionalRequestFilters: [],
  additionalRequestActivities: [],
  additionalRequestId: '',
  approvalLevel: null,
  selectedReasonForRequest: '-1',
  previousOptions: null,
  additionalsDraftData: null,
  isAcknowledged: false,
  isSubmitted: false,
  additionalAttachments: [],
  isCustomerChange: false,
  sinNotAvailable: false,
  requisitionId: null,
  requisitionNumber: null,
  delinquencyCheck: null,
  requisitionCreateCheckbox: false,
  additionalsRequisitionState: {},
};

const additionalRequestSclice = createSlice({
  name: 'additionalRequestSclice',
  initialState: additionalsInitialState,
  reducers: {
    resetAdditionalsState: (state, action) => {
      return {
        ...additionalsInitialState,
        ...action.payload && {
          additionalRequestFilters: state.additionalRequestFilters,
          additionalFilters: state.additionalFilters
        }
      };
    },
    setCurrentAdditionalAction: (state, action) => {
      return { ...state, currentAdditionalAction: action.payload };
    },
    setAdditionalsFilters: (state, action) => {
      return { ...state, additionalFilters: action.payload };
    },
    setPaginationState: (state, action) => {
      return { ...state, paginationState: action.payload };
    },
    setCustomerAccountList: (state, action) => {
      return { ...state, customerAccountList: action.payload };
    },
    setSelectedCustomerAccount: (state, action) => {
      return { ...state, selectedCustomerAccount: action.payload };
    },

    changeAdditionalsStep: (state, action) => {
      return { ...state, currentStep: action.payload };
    },
    changeGarageAddress: (state, action) => {
      return { ...state, garageAddress: action.payload };
    },
    changeProjectedTowingWeight: (state, action) => {
      return { ...state, projectedTowingWeight: action.payload };
    },
    setSelectedStandardItem: (state, action) => {
      return { ...state, selectedStandardItem: action.payload };
    },
    setUseSameSin: (state, action) => {
      return { ...state, useSameSin: action.payload };
    },
    setUsedForTowing: (state, action) => {
      return { ...state, usedForTowing: action.payload };
    },
    setAdditionalRequestId: (state, action) => {
      return { ...state, additionalRequestId: action.payload };
    },
    setAdditionalsRequestStatus: (state, action) => {
      return { ...state, status: action.payload };
    },
    setSelectedAdditionalOptions: (state, action) => {
      return { ...state, selectedOptions: action.payload };
    },
    setLastSin: (state, action) => {
      return { ...state, lastSin: action.payload };
    },
    setSelectedReason: (state, action) => {
      return { ...state, selectedReasonForRequest: action.payload };
    },
    setPreviousOptions: (state, action) => {
      return { ...state, previousOptions: action.payload };
    },
    addSelectedOption: (state, action) => {
      const selectedOption = state.selectedOptions.find(
        ({ option }) =>
          option.equipmentCode.code ===
          action.payload.option.equipmentCode.code,
      );

      if (selectedOption) {
        return state;
      }

      return {
        ...state,
        selectedOptions: [...state.selectedOptions, action.payload],
      };
    },
    updateCommentForSelectedOption: (state, action) => {
      const selectedOption = state.selectedOptions.find(
        ({ option }) =>
          option.equipmentCodeId === action.payload.option.equipmentCodeId,
      );
      if (!selectedOption) return state;

      selectedOption.comment = action.payload.comment;
      return state;
    },
    removeCommentFromSelectedOption: (state, action) => {
      const selectedOption = state.selectedOptions.find(
        ({ option }) =>
          option.equipmentCodeId === action.payload.option.equipmentCodeId,
      );
      if (!selectedOption) return state;

      selectedOption.comment = null;
      return state;
    },
    removeSelectedOption: (state, action) => {
      return {
        ...state,
        selectedOptions: [
          ...state.selectedOptions.filter(({ option, equipmentCode }) => {
            if (equipmentCode && action.payload.equipmentCode) {
              return equipmentCode !== action.payload.equipmentCode;
            } else if (
              action.payload.option &&
              option.equipmentCodeId &&
              action.payload.option.equipmentCodeId
            ) {
              return (
                option.equipmentCodeId !== action.payload.option.equipmentCodeId
              );
            }

            return true;
          }),
        ],
      };
    },
    changeSelectedOptionForCategory: (state, action) => {
      return {
        ...state,
        selectedOptions: [
          ...state.selectedOptions.filter(({ option, equipmentCode }) => {
            if (equipmentCode) return false;
            return (
              option.equipmentCode.category !==
              action.payload.option.equipmentCode.category
            );
          }),
          action.payload,
        ],
      };
    },
    updateSelectedOption: (state, action) => {
      const selectedOptions = state.selectedOptions
        .filter(Boolean)
        .map((option) => {
          if (
            option.equipmentCode === action.payload.option?.equipmentCode?.code
          ) {
            return action.payload;
          } else {
            return option;
          }
        });
      return { ...state, selectedOptions };
    },
    setAdditionalsDraftData: (state, action) => {
      return { ...state, additionalsDraftData: action.payload };
    },
    setVehicleAcknowledged: (state, action) => {
      return { ...state, isAcknowledged: action.payload };
    },
    setSubmitted: (state, action) => {
      return { ...state, isSubmitted: action.payload };
    },
    setAdditionalRequestList: (state, action) => {
      return { ...state, additionalRequestList: action.payload };
    },
    setAdditionalRequestFilters: (state, action) => {
      return { ...state, additionalRequestFilters: action.payload };
    },

    setAdditionalAttachments: (state, action) => {
      return { ...state, additionalAttachments: action.payload };
    },
    addAdditionalAttachment: (state, action) => {
      return {
        ...state,
        additionalAttachments: [...state.additionalAttachments, action.payload],
      };
    },
    updateAdditionalAttachment: (state, action) => {
      return {
        ...state,
        additionalAttachments: state.additionalAttachments.map((attachment) => {
          if (
            attachment.additionalRequestAttachmentId ===
            action.payload.additionalRequestAttachmentId
          ) {
            return action.payload;
          }
          return attachment;
        }),
      };
    },
    removeAdditionalAttachment: (state, action) => {
      return {
        ...state,
        additionalAttachments: [
          ...state.additionalAttachments.filter(
            ({ additionalRequestAttachmentId }) =>
              additionalRequestAttachmentId !==
              action.payload.additionalRequestAttachmentId,
          ),
        ],
      };
    },
    setSupplementaryInformation: (state, action) => {
      return { ...state, supplementaryInformation: action.payload };
    },
    setAdditionalRequestActivities: (state, action) => {
      return { ...state, additionalRequestActivities: action.payload };
    },
    setCustomerChange: (state, action) => {
      return { ...state, isCustomerChange: action.payload };
    },
    setSinNotAvailable: (state, action) => {
      return { ...state, sinNotAvailable: action.payload };
    },
    setAgencyGuidanceDocument: (state, action) => {
      return { ...state, agencyGuidanceDocument: action.payload };
    },
    setRequisitionId: (state, action) => {
      return { ...state, requisitionId: action.payload };
    },
    setRequisitionNumber: (state, action) => {
      return { ...state, requisitionNumber: action.payload };
    },
    setDeliquencyCheck: (state, action) => {
      return { ...state, delinquencyCheck: action.payload };
    },
    setAdditionalsRequisitionState: (state, action) => {
      return { ...state, additionalsRequisitionState: action.payload };
    },
    setRequisitionCreateCheckbox: (state, action) => {
      return { ...state, requisitionCreateCheckbox: action.payload };
    },
  },
});

export const {
  resetAdditionalsState,
  setAdditionalsFilters,
  setPaginationState,
  setCustomerAccountList,
  setSelectedCustomerAccount,
  changeGarageAddress,
  changeProjectedTowingWeight,
  changeAdditionalsStep,
  setAdditionalsRequestStatus,
  setLastSin,
  setAdditionalRequestId,
  setSelectedAdditionalOptions,
  setSelectedStandardItem,
  setUsedForTowing,
  setUseSameSin,
  setSelectedReason,
  addSelectedOption,
  updateCommentForSelectedOption,
  removeCommentFromSelectedOption,
  removeSelectedOption,
  changeSelectedOptionForCategory,
  updateSelectedOption,
  setPreviousOptions,
  setCurrentAdditionalAction,
  setAdditionalsDraftData,
  setVehicleAcknowledged,
  setSubmitted,
  setAdditionalRequestList,
  setAdditionalRequestFilters,
  addAdditionalAttachment,
  updateAdditionalAttachment,
  removeAdditionalAttachment,
  setAdditionalAttachments,
  setSupplementaryInformation,
  setAdditionalRequestActivities,
  setCustomerChange,
  setSinNotAvailable,
  setAgencyGuidanceDocument,
  setRequisitionId,
  setRequisitionNumber,
  setAdditionalsRequisitionState,
  setDeliquencyCheck,
  setRequisitionCreateCheckbox,
} = additionalRequestSclice.actions;

export default additionalRequestSclice.reducer;
